import EngineShell, { type SubTabDef } from '../components/EngineShell';
import DataIntervention, {
  ActionIntervention, MetricTiles, MiniTable, StatusPill,
} from '../components/engine/DataIntervention';
import { api } from '../api/client';
import { useState } from 'react';
import ForecastTripwirePanel from '../components/evolution/ForecastTripwirePanel';
import { C2_EVOLUTION_ENABLED } from '../evolutionFlag';

/**
 * Engine 3 · Manufacturing Operations (CPG value chain · stage 3).
 * The COGS lever. Capacity Utilization is the lighthouse. All sub-tabs live.
 * This file is the gold-standard template for the data-driven engine shells.
 */

interface LineOEE { line_id: string; plant: string; sku_family: string; oee: number; availability: number; performance: number; quality: number; status: string }
interface OEEResp { oee: number; availability: number; performance: number; quality: number; by_line: LineOEE[] }

interface CapLine { line_id: string; plant: string; capacity_cases_M: number; scheduled_cases_M: number; utilization_pct: number; idle_cases_M: number }
interface CapResp { utilization_pct: number; idle_cost_usd_M: number; by_line: CapLine[] }

interface YieldLine { line_id: string; plant: string; yield_pct: number; material_waste_pct: number; scrap_cases_k: number }
interface YieldResp { yield_pct: number; material_waste_pct: number; by_line: YieldLine[] }

interface EnergyLine { line_id: string; plant: string; energy_per_case_kwh: number; prior_period_kwh: number; trend_pct: number }
interface EnergyResp { energy_per_case_kwh: number; trend_pct: number; by_line: EnergyLine[] }

interface Failure { asset: string; line_id: string; probability: number; window: string; downtime_hrs: number; action: string }
interface PdMResp { predicted_failures: Failure[]; downtime_hrs: number }

interface VisionAlert { line_id: string; station: string; severity: string; defect_code: string; units_flagged: number; action: string }
interface QualityResp { defect_rate: number; vision_alerts: VisionAlert[] }

interface ChangeoverLine { line_id: string; current_sku: string; next_sku: string; target_min: number; actual_min: number; variance_min: number }
interface ChangeoverResp { lines: ChangeoverLine[] }

interface RecallStep { batch_id: string; line_id: string; lot_id: string; supplier: string; station: string; timestamp: string; status: string }
interface RecallResp { risk_level: string; affected_units: number; traceback: RecallStep[] }

const pct = (v: number) => `${v.toFixed(1)}%`;

export default function Engine3Shell() {
  const subTabs: SubTabDef[] = [
    ...(C2_EVOLUTION_ENABLED ? [{
      id: 'projected_state', name: 'Projected State', tier: 'lighthouse' as const, moat: 'core' as const, enabled: true,
      render: () => <ForecastTripwirePanel engineId={3} />,
    }] : []),
    {
      id: 'capacity_utilization', name: 'Capacity Utilization', tier: 'lighthouse', moat: 'core', enabled: true,
      render: () => (
        <DataIntervention<CapResp>
          objective="O3 · run the COGS lever — fill hot-fill lines, kill idle cost"
          workflow={['Pull scheduled vs nameplate by line', 'Compute utilization + idle cases', 'Price idle capacity', 'Re-slot under-run lines']}
          decisionRules={{ rules: ['Flag any line < 80% utilization', 'Escalate if plant idle cost > $1M/qtr'] }}
          aiLogic={{ description: 'Deterministic capacity rollup across lines from the plant schedule.', modality: 'deterministic', hitlLevel: 'L3 approval gating', moat: 'core' }}
          kpi={{ primary: 'Utilization % + idle cost ($M)', downstream: 'Feeds Profit-at-Risk cost stack', owner: 'COO / Plant Ops' }}
          queryKey={['mfg', 'capacity']} fetcher={() => api.get<CapResp>('/api/manufacturing/capacity-utilization')}
          render={(d) => (
            <>
              <MetricTiles tiles={[
                { label: 'Network utilization', value: pct(d.utilization_pct), color: d.utilization_pct >= 85 ? 'olive' : 'gold' },
                { label: 'Idle cost', value: `$${d.idle_cost_usd_M.toFixed(1)}M`, color: 'red' },
                { label: 'Lines', value: String(d.by_line.length), color: 'ink' },
              ]} />
              <MiniTable title="By line" rows={d.by_line} rowKey={(r) => r.line_id}
                columns={[
                  { header: 'Line', cell: (r) => <><b className="mono">{r.line_id}</b> · {r.plant}</> },
                  { header: 'Capacity', align: 'right', cell: (r) => <span className="mono">{r.capacity_cases_M.toFixed(0)}M</span> },
                  { header: 'Scheduled', align: 'right', cell: (r) => <span className="mono">{r.scheduled_cases_M.toFixed(1)}M</span> },
                  { header: 'Util', align: 'right', cell: (r) => <span className="mono" style={{ color: r.utilization_pct >= 85 ? 'var(--olive)' : 'var(--gold)' }}>{pct(r.utilization_pct)}</span> },
                  { header: 'Idle', align: 'right', cell: (r) => <span className="mono">{r.idle_cases_M.toFixed(1)}M</span> },
                ]} />
            </>
          )}
        />
      ),
    },
    {
      id: 'oee', name: 'OEE / Line Efficiency', tier: 'flagship', moat: 'core', enabled: true,
      render: () => (
        <DataIntervention<OEEResp>
          objective="O3 · protect throughput — availability × performance × quality"
          workflow={['Stream line telemetry', 'Decompose OEE into A/P/Q', 'Rank worst lines', 'Route losses to PdM or changeover']}
          decisionRules={{ rules: ['Amber if OEE < 80%', 'Quality component must stay ≥ 98%'] }}
          aiLogic={{ description: 'Deterministic OEE decomposition from line counters.', modality: 'deterministic', hitlLevel: 'L2 exception review', moat: 'core' }}
          kpi={{ primary: 'OEE % + A/P/Q split by line', owner: 'COO / Plant Ops' }}
          queryKey={['mfg', 'oee']} fetcher={() => api.get<OEEResp>('/api/manufacturing/oee')}
          render={(d) => (
            <>
              <MetricTiles tiles={[
                { label: 'Network OEE', value: pct(d.oee), color: d.oee >= 80 ? 'olive' : 'gold' },
                { label: 'Availability', value: pct(d.availability), color: 'ink' },
                { label: 'Performance', value: pct(d.performance), color: 'ink' },
                { label: 'Quality', value: pct(d.quality), color: d.quality >= 98 ? 'olive' : 'red' },
              ]} />
              <MiniTable title="By line" rows={d.by_line} rowKey={(r) => r.line_id}
                columns={[
                  { header: 'Line', cell: (r) => <><b className="mono">{r.line_id}</b> · {r.sku_family}</> },
                  { header: 'OEE', align: 'right', cell: (r) => <span className="mono">{pct(r.oee)}</span> },
                  { header: 'Avail', align: 'right', cell: (r) => <span className="mono">{pct(r.availability)}</span> },
                  { header: 'Perf', align: 'right', cell: (r) => <span className="mono">{pct(r.performance)}</span> },
                  { header: 'Status', align: 'right', cell: (r) => <StatusPill status={r.status} /> },
                ]} />
            </>
          )}
        />
      ),
    },
    {
      id: 'yield_waste', name: 'Yield & Waste', tier: 'flagship', moat: 'core', enabled: true,
      render: () => (
        <DataIntervention<YieldResp>
          objective="O3 · convert input to sellable cases — minimize scrap"
          workflow={['Measure yield + material waste by line', 'Identify scrap drivers', 'Target the worst line', 'Feed recall traceability']}
          decisionRules={{ rules: ['Amber if yield < 94%', 'Investigate if material waste > 3.5%'] }}
          aiLogic={{ description: 'Deterministic mass-balance yield computation per line.', modality: 'deterministic', hitlLevel: 'L2 exception review', moat: 'core' }}
          kpi={{ primary: 'Yield % + material waste % + scrap cases', owner: 'COO / Plant Ops' }}
          queryKey={['mfg', 'yield']} fetcher={() => api.get<YieldResp>('/api/manufacturing/yield-waste')}
          render={(d) => (
            <>
              <MetricTiles tiles={[
                { label: 'Network yield', value: pct(d.yield_pct), color: d.yield_pct >= 94 ? 'olive' : 'gold' },
                { label: 'Material waste', value: pct(d.material_waste_pct), color: d.material_waste_pct <= 3 ? 'olive' : 'gold' },
              ]} />
              <MiniTable title="By line" rows={d.by_line} rowKey={(r) => r.line_id}
                columns={[
                  { header: 'Line', cell: (r) => <><b className="mono">{r.line_id}</b> · {r.plant}</> },
                  { header: 'Yield', align: 'right', cell: (r) => <span className="mono">{pct(r.yield_pct)}</span> },
                  { header: 'Waste', align: 'right', cell: (r) => <span className="mono">{pct(r.material_waste_pct)}</span> },
                  { header: 'Scrap', align: 'right', cell: (r) => <span className="mono">{r.scrap_cases_k.toFixed(0)}k</span> },
                ]} />
            </>
          )}
        />
      ),
    },
    {
      id: 'predictive_maintenance', name: 'Predictive Maintenance', tier: 'flagship', moat: 'core', enabled: true,
      render: () => (
        <DataIntervention<PdMResp>
          objective="O3 · prevent unplanned downtime — fix before failure"
          workflow={['Stream sensor telemetry per asset', 'ML predicts failure probability + window', 'Schedule controlled maintenance', 'Avoid the run-killing breakdown']}
          decisionRules={{ rules: ['Schedule PdM if failure probability > 0.3', 'Block the run if predicted within the window'] }}
          aiLogic={{ description: 'ML on time-series sensor data; ranked by failure probability × downtime.', modality: 'ml', hitlLevel: 'L2 exception review', moat: 'core' }}
          kpi={{ primary: 'Predicted failures + downtime hours at risk', owner: 'COO / Maintenance' }}
          queryKey={['mfg', 'pdm']} fetcher={() => api.get<PdMResp>('/api/manufacturing/predictive-maintenance')}
          render={(d) => (
            <>
              <MetricTiles tiles={[
                { label: 'Downtime at risk', value: `${d.downtime_hrs.toFixed(1)} hrs`, color: 'red' },
                { label: 'Predicted failures', value: String(d.predicted_failures.length), color: 'gold' },
              ]} />
              <MiniTable title="Predicted failures" rows={d.predicted_failures} rowKey={(r) => r.asset}
                columns={[
                  { header: 'Asset', cell: (r) => <><b>{r.asset}</b> <span className="mono small faint">{r.line_id}</span></> },
                  { header: 'Prob', align: 'right', cell: (r) => <span className="mono" style={{ color: r.probability > 0.3 ? 'var(--red)' : 'var(--gold)' }}>{(r.probability * 100).toFixed(0)}%</span> },
                  { header: 'Window', align: 'right', cell: (r) => <span className="mono">{r.window}</span> },
                  { header: 'Downtime', align: 'right', cell: (r) => <span className="mono">{r.downtime_hrs}h</span> },
                ]} />
              {d.predicted_failures[0] && <div className="small" style={{ color: 'var(--muted)' }}>Next action: {d.predicted_failures[0].action}</div>}
            </>
          )}
        />
      ),
    },
    {
      id: 'inline_quality', name: 'Inline Quality', tier: 'flagship', moat: 'core', enabled: true,
      render: () => (
        <DataIntervention<QualityResp>
          objective="O3 · catch defects inline before they ship"
          workflow={['Run machine-vision at each station', 'Flag defects by code', 'Hold + inspect flagged pallets', 'Escalate to recall check if systemic']}
          decisionRules={{ rules: ['Hold line if defect rate > 1.5%', 'Red vision alert → pull last two pallets'] }}
          aiLogic={{ description: 'Machine-vision defect detection per station; deterministic hold rules.', modality: 'ml', hitlLevel: 'L2 exception review', moat: 'core' }}
          kpi={{ primary: 'Defect rate + open vision alerts', downstream: 'Feeds Recall Prevention', owner: 'QA / Plant Ops' }}
          queryKey={['mfg', 'quality']} fetcher={() => api.get<QualityResp>('/api/manufacturing/quality')}
          render={(d) => (
            <>
              <MetricTiles tiles={[
                { label: 'Defect rate', value: pct(d.defect_rate * 100), color: d.defect_rate < 0.015 ? 'olive' : 'red' },
                { label: 'Open vision alerts', value: String(d.vision_alerts.length), color: 'gold' },
              ]} />
              <MiniTable title="Vision alerts" rows={d.vision_alerts} rowKey={(r, i) => `${r.line_id}-${i}`}
                columns={[
                  { header: 'Line · station', cell: (r) => <><b className="mono">{r.line_id}</b> · {r.station}</> },
                  { header: 'Code', cell: (r) => <span className="mono small">{r.defect_code}</span> },
                  { header: 'Flagged', align: 'right', cell: (r) => <span className="mono">{r.units_flagged}</span> },
                  { header: 'Severity', align: 'right', cell: (r) => <StatusPill status={r.severity} /> },
                ]} />
            </>
          )}
        />
      ),
    },
    {
      id: 'recall_prevention', name: 'Recall Prevention', tier: 'flagship', moat: 'core', enabled: true,
      render: () => <RecallCheck />,
    },
    {
      id: 'changeover', name: 'Changeover Optimization', tier: 'core', moat: 'core', enabled: true,
      render: () => (
        <DataIntervention<ChangeoverResp>
          objective="O3 · shrink changeover loss between SKUs"
          workflow={['Pull changeover schedule', 'Compare actual vs target minutes', 'Target the worst variance', 'Re-sequence to cut losses']}
          decisionRules={{ rules: ['Flag variance > 5 min', 'Lock candidate changeovers 24h ahead'] }}
          aiLogic={{ description: 'Deterministic changeover variance vs SMED targets.', modality: 'deterministic', hitlLevel: 'L2 exception review', moat: 'core' }}
          kpi={{ primary: 'Changeover variance (min) by line', owner: 'Plant scheduler' }}
          queryKey={['mfg', 'changeover']} fetcher={() => api.get<ChangeoverResp>('/api/manufacturing/changeover')}
          render={(d) => (
            <MiniTable title="Changeover variance" rows={d.lines} rowKey={(r) => r.line_id}
              columns={[
                { header: 'Line', cell: (r) => <b className="mono">{r.line_id}</b> },
                { header: 'From → to', cell: (r) => <span className="small">{r.current_sku} → {r.next_sku}</span> },
                { header: 'Target', align: 'right', cell: (r) => <span className="mono">{r.target_min}m</span> },
                { header: 'Actual', align: 'right', cell: (r) => <span className="mono">{r.actual_min}m</span> },
                { header: 'Variance', align: 'right', cell: (r) => <span className="mono" style={{ color: r.variance_min > 5 ? 'var(--red)' : 'var(--olive)' }}>+{r.variance_min}m</span> },
              ]} />
          )}
        />
      ),
    },
    {
      id: 'energy', name: 'Energy per Case', tier: 'core', moat: 'commodity', enabled: true,
      render: () => (
        <DataIntervention<EnergyResp>
          objective="O3 · trim energy cost per case (hygiene)"
          workflow={['Meter energy per case by line', 'Compare to prior period', 'Standardize the heavy lines']}
          decisionRules={{ rules: ['Track trend; outsource benchmarking', 'No strategic gate — commodity hygiene'] }}
          aiLogic={{ description: 'Deterministic energy-per-case metering vs prior period.', modality: 'deterministic', hitlLevel: 'L1 sampling', moat: 'commodity' }}
          kpi={{ primary: 'kWh per case + trend', owner: 'Plant utilities' }}
          queryKey={['mfg', 'energy']} fetcher={() => api.get<EnergyResp>('/api/manufacturing/energy')}
          render={(d) => (
            <>
              <MetricTiles tiles={[
                { label: 'Energy / case', value: `${d.energy_per_case_kwh.toFixed(3)} kWh`, color: 'ink' },
                { label: 'Trend', value: `${d.trend_pct > 0 ? '+' : ''}${d.trend_pct.toFixed(1)}%`, color: d.trend_pct < 0 ? 'olive' : 'gold' },
              ]} />
              <MiniTable title="By line" rows={d.by_line} rowKey={(r) => r.line_id}
                columns={[
                  { header: 'Line', cell: (r) => <><b className="mono">{r.line_id}</b> · {r.plant}</> },
                  { header: 'kWh/case', align: 'right', cell: (r) => <span className="mono">{r.energy_per_case_kwh.toFixed(3)}</span> },
                  { header: 'Trend', align: 'right', cell: (r) => <span className="mono" style={{ color: r.trend_pct < 0 ? 'var(--olive)' : 'var(--gold)' }}>{r.trend_pct.toFixed(1)}%</span> },
                ]} />
            </>
          )}
        />
      ),
    },
  ];

  return (
    <EngineShell
      engineId={3}
      engineName="Engine 3 · Manufacturing Operations"
      engineCode="E3"
      subTabs={subTabs}
      backgrounds={[{ id: 'spec-receipt', name: 'Spec-Receipt Notifier', status: 'ok' }]}
    />
  );
}

/* Recall Prevention — POST with a batch id input */
function RecallCheck() {
  const [batch, setBatch] = useState('B-1042');
  return (
    <ActionIntervention<RecallResp>
      objective="O3 · trace a batch fast — contain before it spreads"
      workflow={['Enter a batch id', 'Trace lot → supplier → station', 'Score recall risk', 'Contain affected units']}
      decisionRules={{ rules: ['Red risk → hold + notify QA', 'Tie every unit to a supplier lot'] }}
      aiLogic={{ description: 'Deterministic forward/back traceability across the lot graph.', modality: 'deterministic', hitlLevel: 'L3 approval gating', moat: 'core' }}
      kpi={{ primary: 'Recall risk + affected units + traceback', owner: 'QA / Plant Ops' }}
      initialState={{
        risk_level: 'amber',
        affected_units: 4200,
        traceback: [{ batch_id: batch, line_id: 'NJ-L2', lot_id: 'LOT-883', supplier: 'Vobev', station: 'filler', timestamp: '2026-05-29T08:00:00Z', status: 'hold' }],
      }}
      input={
        <>
          <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Batch id</div>
          <input className="c2-input" value={batch} onChange={(e) => setBatch(e.target.value)} style={{ width: 200 }} />
        </>
      }
      runLabel="Trace batch"
      action={() => api.manufacturingRecallCheck(batch) as Promise<RecallResp>}
      render={(d) => (
        <>
          <MetricTiles tiles={[
            { label: 'Recall risk', value: d.risk_level, color: d.risk_level === 'red' ? 'red' : d.risk_level === 'amber' ? 'gold' : 'olive' },
            { label: 'Affected units', value: d.affected_units.toLocaleString(), color: 'red' },
          ]} />
          <MiniTable title="Traceback" rows={d.traceback} rowKey={(r, i) => `${r.lot_id}-${i}`}
            columns={[
              { header: 'Station', cell: (r) => <b>{r.station}</b> },
              { header: 'Lot', cell: (r) => <span className="mono small">{r.lot_id}</span> },
              { header: 'Supplier', cell: (r) => <span className="small">{r.supplier}</span> },
              { header: 'Status', align: 'right', cell: (r) => <StatusPill status={r.status} /> },
            ]} />
        </>
      )}
    />
  );
}
